import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, money, errMsg } from "@/lib/api";
import { StatusChip, BureauChip, TimerBadge } from "@/components/StatusChip";
import { CheckCircle2, Circle, ArrowRight, ShieldCheck, TrendingUp, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const LOCK_IMG = "https://images.unsplash.com/photo-1633265486064-086b219458ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjcmVkaXQlMjBjYXJkJTIwc2VjdXJpdHklMjBsb2NrfGVufDB8fHx8MTc4OTE0MzYzNnww&ixlib=rb-4.1.0&q=85";

export default function PortalHome() {
  const [data, setData] = useState(null);
  const load = () => api.get("/portal/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const flag = async (item, notMine) => {
    try {
      await api.patch(`/portal/items/${item.id}/flag`, { not_mine: notMine });
      toast.success(notMine ? `Flagged ${item.creditor} as not yours — your case team has been notified` : "Flag removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading your progress…</div>;
  const { client, steps, disputes, items } = data;
  const first = client.first_name || "there";
  const scores = client.scores || {};
  const activeDisputes = disputes.filter((d) => ["mailed", "in_review"].includes(d.status));
  const nextStep = steps.find((s) => !s.done);
  const stepLinks = { intake: "/portal/onboarding", documents: "/portal/documents", agreement: "/portal/sign", payment: "/portal/billing" };
  const disputedItems = items.filter((i) => ["disputed", "confirmed"].includes(i.status));
  const resolvedItems = items.filter((i) => i.status === "resolved");
  const openItems = items.filter((i) => i.status !== "resolved");
  const flaggedCount = items.filter((i) => i.client_flag === "not_mine").length;

  return (
    <div className="space-y-8" data-testid="portal-home">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Welcome back, {first}.</h1>
        <p className="text-slate-600 mt-2">
          {activeDisputes.length > 0
            ? `Your credit journey is on track — we're actively disputing ${disputedItems.length} item${disputedItems.length === 1 ? "" : "s"} on your behalf.`
            : "Let's get your credit journey moving. Complete the steps below and we'll take it from there."}
        </p>
      </div>

      {(scores.start || scores.current) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="client-score-tracker">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-slate-800">Your score journey</h2>
            {scores.start && scores.current && (
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                <TrendingUp className="h-4 w-4" /> +{scores.current - scores.start} pts since start
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[["Where you started", scores.start], ["Where you are", scores.current], ["Your goal", scores.goal]].map(([label, val], idx) => (
              <div key={label} className={`rounded-lg p-4 text-center ${idx === 1 ? "bg-[#0B2545] text-white" : "bg-slate-50"}`}>
                <div className={`text-3xl font-extrabold font-heading ${idx === 1 ? "text-white" : "text-slate-800"}`}>{val ?? "—"}</div>
                <div className={`text-xs mt-1 ${idx === 1 ? "text-sky-200" : "text-slate-500"}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nextStep && (
        <Link to={stepLinks[nextStep.key]} data-testid="next-step-card"
          className="block rounded-xl border-2 border-blue-200 bg-blue-50 p-5 hover:border-blue-400 transition-colors duration-150">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-blue-600 mb-1">Your next step</div>
              <div className="font-heading font-semibold text-lg text-slate-900">{nextStep.label}</div>
            </div>
            <ArrowRight className="h-6 w-6 text-blue-600" />
          </div>
        </Link>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-semibold text-slate-800 mb-4">Your setup checklist</h2>
        <div className="space-y-3">
          {steps.map((s, idx) => (
            <Link key={s.key} to={stepLinks[s.key]} data-testid={`step-${s.key}`}
              className="flex items-center gap-3 rounded-lg border border-slate-100 px-4 py-3 hover:border-blue-200 hover:bg-blue-50/40 transition-colors duration-150">
              {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
              <span className={`text-sm ${s.done ? "text-slate-400 line-through" : "text-slate-800 font-medium"}`}>{idx + 1}. {s.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {disputes.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-semibold text-slate-800 mb-1">Items in dispute</h2>
          <p className="text-sm text-slate-500 mb-4">We've sent these to the bureaus by certified mail. By law, they must respond within 30 days. <Link to="/portal/guides#fraud" className="text-rose-700 font-medium hover:underline" data-testid="home-fraud-guide-link">Don't recognize one of these? It may be fraud →</Link></p>
          <div className="space-y-3">
            {disputes.flatMap((d) => (d.items || []).map((i) => (
              <div key={`${d.id}-${i.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-slate-800">{i.creditor}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {i.status === "resolved"
                      ? `${i.resolution === "deleted" ? "Removed from your report" : i.resolution === "verified" ? "Bureau claims it's verified — we're on the next round" : "Updated"}`
                      : `Disputed ${fmtDate(d.mailed_at)} — waiting on ${data.bureaus[d.bureau] || d.bureau}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BureauChip bureau={d.bureau} />
                  {i.status === "resolved"
                    ? <StatusChip status="resolved" />
                    : <TimerBadge daysLeft={d.days_left} status={d.status} />}
                </div>
              </div>
            )))}
          </div>
          {resolvedItems.length > 0 && (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
              {resolvedItems.filter((i) => i.resolution === "deleted").length} item(s) deleted from your reports so far. That's real progress.
            </div>
          )}
        </div>
      )}

      {openItems.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="portal-items-review">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Items found on your reports</h2>
            {flaggedCount > 0 && <span className="text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2.5 py-1" data-testid="flagged-count">{flaggedCount} flagged as not mine</span>}
          </div>
          <p className="text-sm text-slate-500 mb-4">Recognize each one? If an account isn't yours, tap <strong>This isn't mine</strong> — your case team is alerted instantly and we'll switch to the identity-theft block process. <Link to="/portal/guides#fraud" className="text-rose-700 font-medium hover:underline">How to report it to the FTC →</Link></p>
          <div className="space-y-2">
            {openItems.map((i) => {
              const notMine = i.client_flag === "not_mine";
              return (
                <div key={i.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-3 ${notMine ? "border-rose-200 bg-rose-50/40" : "border-slate-100"}`} data-testid={`portal-item-${i.id.slice(0, 8)}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">{i.creditor}
                      {notMine && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700"><ShieldAlert className="h-3 w-3" /> Not mine</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize">{(i.type || "").replace("_", " ")} {i.account_number ? `· ${i.account_number}` : ""} {i.balance ? `· ${money(i.balance)}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <BureauChip bureau={i.bureau} />
                    <button onClick={() => flag(i, !notMine)}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors duration-150 ${notMine ? "bg-white text-slate-600 border-slate-200 hover:bg-slate-50" : "bg-rose-600 text-white border-rose-600 hover:bg-rose-700"}`}
                      data-testid={`not-mine-btn-${i.id.slice(0, 8)}`}>
                      {notMine ? "Actually, this is mine" : "This isn't mine"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4">
        <img src={LOCK_IMG} alt="Bank-level security" className="h-16 w-16 rounded-lg object-cover hidden sm:block" />
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Your documents and personal information are encrypted in transit and at rest. Only your assigned case team can access your file, and every view is logged.
          </p>
        </div>
      </div>

      {data.invoices.some((i) => i.status === "pending") && (
        <div className="text-sm text-slate-500">
          You have a pending invoice — <Link to="/portal/billing" className="text-blue-700 font-medium hover:underline" data-testid="goto-billing-link">view billing</Link>.
        </div>
      )}
      <div className="hidden">{money(0)}</div>
    </div>
  );
}
